import { useCallback } from 'react';
import { AlertType } from '../components';
import {
  useApplyTagsMutation,
  useArchivePracticeProspectMutation,
  useDeletePracticeProspectMutation,
  useEditPracticeProspectMutation,
  useFavoriteProspectMutation,
  useReactivatePracticeProspectMutation,
  useUnfavoriteProspectMutation,
} from '../services';
import { EditPracticeProspectPayload, UpdatedPracticeProspectData } from '../types';
import useHandleApiResponse from './useHandleApiResponse';
import useToast from './useToast';

type BaseActionPayload = {
  id: string;
  onError?: () => void;
  onSuccess?: () => void;
};

const useSimActions = () => {
  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  const [applyTags, { isLoading: isApplyingTags }] = useApplyTagsMutation();
  const [archivePracticeProspect, { isLoading: isArchiving }] = useArchivePracticeProspectMutation();
  const [deletePracticeProspect, { isLoading: isDeleting }] = useDeletePracticeProspectMutation();
  const [editPracticeProspect, { isLoading: isEditing }] = useEditPracticeProspectMutation();
  const [favoriteProspect, { isLoading: isFavoriteLoading }] = useFavoriteProspectMutation();
  const [unfavoriteProspect, { isLoading: isUnfavoriteLoading }] = useUnfavoriteProspectMutation();
  const [reactivatePracticeProspect, { isLoading: isReactivating }] = useReactivatePracticeProspectMutation();

  const runApplyTags = useCallback(
    async ({
      id,
      tagsToApply,
      tagsToRemove,
      onError,
      onSuccess,
    }: {
      tagsToApply: string[];
      tagsToRemove: string[];
    } & BaseActionPayload) => {
      // If there are no tags to apply or remove, return.
      if (!tagsToApply.length && !tagsToRemove.length) {
        onSuccess?.();
        return;
      }

      // Otherwise, we need to update the prospect tags.
      try {
        const response = await applyTags({ id, tagsToApply, tagsToRemove });
        handleApiResponse({ response, onError, onSuccess });
      } catch (error) {
        console.error('Failed to update simulation tags', error);
        onError?.();
      }
    },
    [applyTags, handleApiResponse, showToast]
  );

  const runArchive = useCallback(
    async ({ id, onError, onSuccess }: BaseActionPayload) => {
      try {
        const response = await archivePracticeProspect(id);
        handleApiResponse({ response, onError, onSuccess });
      } catch (error) {
        console.error(error);
        onError?.();
        showToast({ message: 'Failed to archive simulation', type: AlertType.ERROR });
      }
    },
    [archivePracticeProspect, handleApiResponse, showToast]
  );

  const runDelete = useCallback(
    async ({ id, onError, onSuccess }: BaseActionPayload) => {
      try {
        const response = await deletePracticeProspect(id);
        handleApiResponse({ response, onError, onSuccess });
      } catch (error) {
        console.error(error);
        onError?.();
        showToast({ message: 'Failed to delete simulation', type: AlertType.ERROR });
      }
    },
    [deletePracticeProspect, handleApiResponse, showToast]
  );

  const runEdit = useCallback(
    async ({
      payload,
      onError,
      onSuccess,
    }: {
      payload: EditPracticeProspectPayload;
      onError?: () => void;
      onSuccess?: (data: UpdatedPracticeProspectData) => void;
    }) => {
      try {
        const response = await editPracticeProspect(payload);
        handleApiResponse({ response, onError, onSuccess });
      } catch (error) {
        console.error(error);
        onError?.();
        showToast({ message: 'Failed to edit simulation', type: AlertType.ERROR });
      }
    },
    [editPracticeProspect, handleApiResponse, showToast]
  );

  const runFavorite = useCallback(
    async ({ id, onError, onSuccess }: BaseActionPayload) => {
      try {
        const response = await favoriteProspect(id);
        handleApiResponse({ response, onError, onSuccess });
      } catch (error) {
        console.error('Failed to add simulation to favorites', error);
        onError?.();
        showToast({ message: 'Failed to add simulation to favorites', type: AlertType.ERROR });
      }
    },
    [favoriteProspect, handleApiResponse, showToast]
  );

  const runUnfavorite = useCallback(
    async ({ id, onError, onSuccess }: BaseActionPayload) => {
      try {
        const response = await unfavoriteProspect(id);
        handleApiResponse({ response, onError, onSuccess });
      } catch (error) {
        console.error('Failed to remove simulation from favorites', error);
        onError?.();
        showToast({ message: 'Failed to remove simulation from favorites', type: AlertType.ERROR });
      }
    },
    [unfavoriteProspect, handleApiResponse, showToast]
  );

  const runReactivate = useCallback(
    async ({ id, onError, onSuccess }: BaseActionPayload) => {
      try {
        const response = await reactivatePracticeProspect(id);
        handleApiResponse({ response, onError, onSuccess });
      } catch (error) {
        console.error('Failed to reactivate simulation', error);
        onError?.();
        showToast({ message: 'Failed to reactivate simulation', type: AlertType.ERROR });
      }
    },
    [reactivatePracticeProspect, handleApiResponse, showToast]
  );

  return {
    runApplyTags,
    runArchive,
    runDelete,
    runEdit,
    runFavorite,
    runUnfavorite,
    runReactivate,
    isApplyingTags,
    isArchiving,
    isDeleting,
    isEditing,
    isFavoriteLoading,
    isReactivating,
    isUnfavoriteLoading,
  };
};

export default useSimActions;
